/** Selector of the top-bar control that opens window `id` (avatar, bell...). */
export const morphTriggerSelector = (id: string): string =>
  `[data-morph-trigger="${id}"]`;

export interface ViewportBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CloseButtonPlacement {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The trigger's on-screen box without its transforms. Its center comes from
 * getBoundingClientRect (scale and rotate keep the center in place) and its
 * size from the layout box, so it stays right while the avatar is scaled down
 * behind the open window.
 */
export const untransformedBox = (
  rect: Pick<DOMRect, "left" | "top" | "width" | "height">,
  layoutWidth: number,
  layoutHeight: number,
): ViewportBox => ({
  left: rect.left + rect.width / 2 - layoutWidth / 2,
  top: rect.top + rect.height / 2 - layoutHeight / 2,
  width: layoutWidth,
  height: layoutHeight,
});

/**
 * Where the close button goes inside a window (whose resting top-left corner
 * is `windowLeft`, `windowTop` in the viewport) so that, at rest, it covers
 * the trigger exactly: the trigger appears to turn into the x.
 */
export const closeButtonPlacement = (
  trigger: ViewportBox,
  windowLeft: number,
  windowTop: number,
): CloseButtonPlacement => ({
  left: trigger.left - windowLeft,
  top: trigger.top - windowTop,
  width: trigger.width,
  height: trigger.height,
});

/** Measures a trigger, or null when it is not on screen. */
export const measureTrigger = (selector: string): ViewportBox | null => {
  const trigger = document.querySelector<HTMLElement>(selector);
  if (!trigger || !trigger.offsetWidth) return null;
  return untransformedBox(
    trigger.getBoundingClientRect(),
    trigger.offsetWidth,
    trigger.offsetHeight,
  );
};
